import { PLANNED, SAME_DAY } from "../consts/collections";
import {
  CHOICE_PLAN_TYPE,
  COLLECTIONS_PLAN_TYPE,
  LOCATIONS_PLAN_TYPE,
  OVERRIDES_PLAN_TYPE,
  PICKUP_PLAN_TYPE,
  ROBOTS_PLAN_TYPE,
} from "../consts/planTypes";
import {
  INVALID_TIME_FORMAT,
  INVALID_STOP_TIME_FORMAT,
  ROUTE_NOT_FOUND,
  INVALID_ROUTE_CODE,
  DRIVER_IS_REQUIRED_FOR_AN_ODF,
  THIS_DRIVER_REQUIRES_AN_ODF,
  DUTY_START_MUST_BE_BEFORE_DUTY_END,
  DUTY_START_MUST_BE_BEFORE_RETURN,
  DUTY_START_MUST_BE_BEFORE_DEPART,
  DUTY_END_MUST_BE_AFTER_DUTY_START,
  DUTY_END_MUST_BE_AFTER_DEPART,
  DUTY_END_MUST_BE_AFTER_RETURN,
  TOTAL_DUTY_MUST_BE_AT_LEAST,
  DEPART_MUST_BE_BEFORE_RETURN,
  DEPART_MUST_BE_AFTER_DUTY_START,
  DEPART_MUST_BE_BEFORE_DUTY_END,
  RETURN_MUST_BE_AFTER_DEPART,
  RETURN_MUST_BE_AFTER_DUTY_START,
  RETURN_MUST_BE_BEFORE_DUTY_END,
  AM_TIME_IN_DEPOT_CANT_BE_GREATER,
  PM_TIME_IN_DEPOT_CANT_BE_GREATER,
  SECTION_IS_REQUIRED,
  TOTAL_DUTY_EXCEEDS_MAX_DUTY,
  SHORT_DAYS,
  NONE,
  INVALID_PHONE_NUMBER,
  PHONE_NUMBER_IS_REQUIRED,
  DRIVER_TYPE_IS_REQUIRED,
  VEHICLE_TYPE_IS_REQUIRED,
  START_DATE_IS_REQUIRED,
  AVERAGE_DELIVERIES_IS_REQUIRED,
  ROUTE_NOT_SUITABLE,
  DRIVER_NOT_SUITABLE,
} from "../consts/strings";
import {
  CHOICE_DRIVER_TYPE,
  DR_DRIVER_TYPE,
  ODF_DRIVER_TYPE,
  ROBOT_DRIVER_TYPE,
} from "../consts/driverTypes";
import { SECTOR } from "../consts/layers";
import {
  getDriverCodeRegexes,
  newDriverCodeRegex,
  phoneNumberRegex,
} from "../consts/driverManagement";

import {
  formatMinutesToHHMM,
  formatSecondsToHHMM,
  getDiffInHHMM,
  getDiffInMinutes,
  isHHMMFormat,
  isMMSSFormat,
} from "./date";
import {
  generateColor,
  isNonRedirectionRouteCode,
  routeHasTerritory,
} from "./routes";
import { Postcode } from "./postcode";

export const getDriverOdf = ({ driverType, driverCode, plannedOdf } = {}) =>
  driverType !== ODF_DRIVER_TYPE ? plannedOdf : driverCode;

export const getUpdateImpactOnMatrixDay = ({
  dayOfWeek,
  driverEdit,
  driverEditCopy,
  drivers,
  routes,
}) => {
  const { impactedRoutes, impactedDrivers } = applyChangeToMatrixDay({
    dayOfWeek,
    driverEdit,
    driverEditCopy,
    drivers,
    routes,
  });
  return {
    driversImpacted: impactedDrivers.reduce((acc, { driverCode }) => {
      acc[driverCode] = true;
      return acc;
    }, {}),
    routesImpacted: impactedRoutes.reduce((acc, { routeCode }) => {
      acc[routeCode] = true;
      return acc;
    }, {}),
  };
};

export const applyChangeToMatrixDay = ({
  dayOfWeek,
  driverEdit,
  driverEditCopy,
  drivers,
  routes,
}) => {
  const impactedDriverCodes = new Set();
  const impactedRouteCodes = new Set();
  drivers = { ...drivers };
  const driversByRouteCode = Object.values(drivers).reduce((acc, driver) => {
    if (driver.routeCode) acc[driver.routeCode] = driver;
    return acc;
  }, {});
  routes = routes.reduce((acc, route) => {
    acc[route.routeCode] = route;
    return acc;
  }, {});

  const oldRoutePreEdit = routes[driverEditCopy.routeCode];
  const oldDriverPreEdit = !!driverEditCopy.driverCode && driverEditCopy;
  const oldDriverPreEditOdf = drivers[oldDriverPreEdit?.plannedOdf];

  const newRoutePreEditDriver =
    drivers[driversByRouteCode[driverEdit.routeCode]?.driverCode];
  const newRoutePreEditOdf =
    drivers[driversByRouteCode[driverEdit.routeCode]?.plannedOdf];

  const newOdfPreEdit = drivers[driverEdit.plannedOdf];
  const newOdfDriverPreEdit = drivers[newOdfPreEdit?.plannedDriverCode];
  const newOdfPreEditRoute =
    routes[(newOdfDriverPreEdit || newOdfPreEdit)?.routeCode];

  const newDriverPreEdit = drivers[driverEdit.driverCode];
  const newDriverPreEditOdf = drivers[newDriverPreEdit?.plannedOdf];
  const newDriverPreEditRoute = routes[newDriverPreEdit?.routeCode];

  const daysOrder = Object.keys(SHORT_DAYS).reduce((acc, i) => {
    acc[SHORT_DAYS[i]] = i;
    return acc;
  }, {});

  const filterToday = ({ workingDays }) =>
    workingDays.filter((day) => day !== SHORT_DAYS[dayOfWeek]);
  const addToday = ({ workingDays }) =>
    !SHORT_DAYS[dayOfWeek]
      ? workingDays
      : [...new Set([...workingDays, SHORT_DAYS[dayOfWeek]])].sort(
          (a, b) => daysOrder[a] - daysOrder[b]
        );

  // 1) driverCode in route allocated to oldDriver before edit - to null
  if (oldRoutePreEdit) {
    impactedRouteCodes.add(oldRoutePreEdit.routeCode);
    routes[oldRoutePreEdit.routeCode] = {
      ...oldRoutePreEdit,
      driverCode: null,
    };
  }

  // 2) routeCode and plannedOdf in driver allocated to the route before edit - to null
  if (oldDriverPreEdit) {
    impactedDriverCodes.add(oldDriverPreEdit.driverCode);
    drivers[oldDriverPreEdit.driverCode] = {
      ...oldDriverPreEdit,
      routeCode: null,
      plannedOdf: null,
      workingDays: filterToday(oldDriverPreEdit),
    };
  }

  // 3) plannedDriverCode in odf owning the driver allocated to the route before edit - to null
  if (oldDriverPreEditOdf) {
    impactedDriverCodes.add(oldDriverPreEditOdf.driverCode);
    drivers[oldDriverPreEditOdf.driverCode] = {
      ...oldDriverPreEditOdf,
      plannedDriverCode: null,
      workingDays: filterToday(oldDriverPreEditOdf),
    };
  }

  // 4) routeCode and plannedOdf in driver allocated to the new route before edit - to null
  if (newRoutePreEditDriver) {
    impactedDriverCodes.add(newRoutePreEditDriver.driverCode);
    drivers[newRoutePreEditDriver.driverCode] = {
      ...newRoutePreEditDriver,
      routeCode: null,
      plannedOdf: null,
      workingDays: filterToday(newRoutePreEditDriver),
    };
  }

  // 5) plannedDriverCode in odf owning the driver allocated to the new route before edit - to null
  if (newRoutePreEditOdf) {
    impactedDriverCodes.add(newRoutePreEditOdf.driverCode);
    drivers[newRoutePreEditOdf.driverCode] = {
      ...newRoutePreEditOdf,
      plannedDriverCode: null,
      workingDays: filterToday(newRoutePreEditOdf),
    };
  }

  // 6) routeCode and plannedOdf in driver allocated to newOfd before edit - to null
  if (newOdfDriverPreEdit) {
    impactedDriverCodes.add(newOdfDriverPreEdit.driverCode);
    drivers[newOdfDriverPreEdit.driverCode] = {
      ...newOdfDriverPreEdit,
      routeCode: null,
      plannedOdf: null,
      workingDays: filterToday(newOdfDriverPreEdit),
    };
  }

  // 7) driverCode in route allocated to driver allocated to newOdf(or odf himself) before edit - to null
  if (newOdfPreEditRoute) {
    impactedRouteCodes.add(newOdfPreEditRoute.routeCode);
    routes[newOdfPreEditRoute.routeCode] = {
      ...newOdfPreEditRoute,
      driverCode: null,
    };
  }

  // 8) plannedDriverCode in odf who owned newDriver before edit - to null
  if (newDriverPreEditOdf) {
    impactedDriverCodes.add(newDriverPreEditOdf.driverCode);
    drivers[newDriverPreEditOdf.driverCode] = {
      ...newDriverPreEditOdf,
      plannedDriverCode: null,
      workingDays: filterToday(newDriverPreEditOdf),
    };
  }

  // 9) driverCode in route allocated to newDriver before edit - to null
  if (newDriverPreEditRoute) {
    impactedRouteCodes.add(newDriverPreEditRoute.routeCode);
    routes[newDriverPreEditRoute.routeCode] = {
      ...newDriverPreEditRoute,
      driverCode: null,
    };
  }

  // 10) plannedDriverCode in odf owning new driver - to that driver driverCode
  if (driverEdit.plannedOdf) {
    impactedDriverCodes.add(driverEdit.plannedOdf);
    drivers[driverEdit.plannedOdf] = {
      ...drivers[driverEdit.plannedOdf],
      routeCode: null,
      plannedDriverCode: driverEdit.driverCode,
      workingDays: addToday(drivers[driverEdit.plannedOdf]),
    };
  }

  // 11) whole driverEdit as newDriver - plannedDriverCode as null
  if (driverEdit.driverCode) {
    impactedDriverCodes.add(driverEdit.driverCode);
    drivers[driverEdit.driverCode] = {
      ...driverEdit,
      plannedDriverCode: null,
      workingDays: addToday(drivers[driverEdit.driverCode]),
    };
  }

  // 12) driverCode in driverEdit route - to new driver driverCode
  if (driverEdit.routeCode) {
    impactedRouteCodes.add(driverEdit.routeCode);
    routes[driverEdit.routeCode] = {
      ...routes[driverEdit.routeCode],
      driverCode: driverEdit.driverCode || null,
    };
  }

  return {
    impactedRoutes: [...impactedRouteCodes].reduce((acc, routeCode) => {
      if (routes[routeCode]) acc.push(routes[routeCode]);
      return acc;
    }, []),
    impactedDrivers: [...impactedDriverCodes].reduce((acc, driverCode) => {
      if (drivers[driverCode]) acc.push(drivers[driverCode]);
      return acc;
    }, []),
    routes: Object.values(routes),
    drivers,
  };
};

// COMMON DRIVER/PLAN MANAGEMENT - DRIVER DAY UPDATE
export const updateDriverDay = async ({
  disableLocking = false, // Used when updating driver for non daily plans
  update: { name, value },
  drivers = {},
  routes = [],
  driverEdit = {},
  driverEditCopy = {},
  getRouteDayLock = (routeCode) => ({}),
  lockRouteDayAction = async (routeCode) => {},
  unlockRouteDayAction = async (routeCode) => {},
  getDriverDayLock = (driverCode) => ({}),
  lockDriverDayAction = async (driverCode) => {},
  unlockDriverDayAction = async (driverCode) => {},
  addNotificationAction = (message) => message,
  onFinally = () => {},
}) => {
  const driversByRouteCode = Object.values(drivers).reduce((acc, driver) => {
    if (driver.routeCode) acc[driver.routeCode] = driver;
    return acc;
  }, {});
  routes = routes.reduce((acc, route) => {
    acc[route.routeCode] = route;
    return acc;
  }, {});

  const {
    driverCode: preEditDriver,
    plannedOdf: preEditOdf,
    routeCode: preEditRoute,
  } = driverEditCopy;
  const {
    driverCode: currentDriver,
    plannedOdf: currentOdf,
    routeCode: currentRoute,
  } = driverEdit;
  const { plannedDriverCode: currentOdfPreEditPlannedDriver } =
    drivers[currentOdf] || {};
  const {
    driverCode: currentRoutePreEditDriver,
    plannedOdf: currentRoutePreEditOdf,
  } = driversByRouteCode[currentRoute] || {};
  const { plannedOdf: currentDriverPreEditPlannedOdf } =
    drivers[currentDriver] || {};

  const onLockRouteDay = async (routeCode) => {
    if (!routes[routeCode]) return;
    const { lockedByYou, lockedInDifferentBrowser, changed } =
      getRouteDayLock(routeCode) || {};
    if (changed) {
      addNotificationAction(`Route ${routeCode} has change.`);
      throw new Error(`Route ${routeCode} has change.`);
    } else if (
      disableLocking ||
      !routeCode ||
      (lockedByYou && !lockedInDifferentBrowser)
    )
      return;
    const { error } = await lockRouteDayAction(routeCode);
    if (error) throw error;
  };

  const onUnlockRouteDay = async (routeCode) => {
    if (!routes[routeCode]) return;
    const routeDayLock = getRouteDayLock(routeCode) || {};
    if (
      disableLocking ||
      !routeCode ||
      preEditRoute === routeCode ||
      !routeDayLock.lockedByYou ||
      routeDayLock.changed
    )
      return;
    await unlockRouteDayAction(routeCode);
  };

  const onLockDriverDay = async (driverCode) => {
    const { lockedByYou, lockedInDifferentBrowser, changed } =
      getDriverDayLock(driverCode) || {};
    if (changed) {
      addNotificationAction(
        `Driver ${driverCode.split("*")[1]} ${
          drivers[driverCode].driverName
        } has change.`
      );
      throw new Error(
        `Driver ${driverCode.split("*")[1]} ${
          drivers[driverCode].driverName
        } has change.`
      );
    } else if (
      disableLocking ||
      !driverCode ||
      (lockedByYou && !lockedInDifferentBrowser)
    )
      return;
    const { error } = await lockDriverDayAction(driverCode);
    if (error) throw error;
  };

  const onUnlockDriverDay = async (driverCode) => {
    const driverDayLock = getDriverDayLock(driverCode) || {};
    if (
      disableLocking ||
      !driverCode ||
      [preEditDriver, preEditOdf].includes(driverCode) ||
      !driverDayLock.lockedByYou ||
      driverDayLock.changed
    )
      return;
    await unlockDriverDayAction(driverCode);
  };

  try {
    let promises = await Promise.allSettled([
      onLockRouteDay(preEditRoute),
      ...[preEditDriver, preEditOdf].map(onLockDriverDay),
    ]);
    let failToLock = promises.find(({ status }) => status !== "fulfilled");
    if (failToLock) throw new Error("Fail to lock");

    let newDriverEdit;
    let driverDayToLock = new Set();
    let driverDayToUnlock = new Set();
    let routeDayToLock = new Set();
    let routeDayToUnlock = new Set();

    if (["plannedOdf", "driverCode"].includes(name)) {
      const { routeCode: currentOdfPreEditRoute } =
        drivers[currentOdfPreEditPlannedDriver] || {};
      const { routeCode: currentDriverPreEditRoute } =
        drivers[currentDriver] || {};
      routeDayToUnlock.add(currentOdfPreEditRoute);
      routeDayToUnlock.add(currentDriverPreEditRoute);
      driverDayToUnlock.add(currentOdfPreEditPlannedDriver);
      driverDayToUnlock.add(currentOdf);
      driverDayToUnlock.add(currentDriverPreEditPlannedOdf);
      driverDayToUnlock.add(currentDriver);

      if (name === "plannedOdf")
        newDriverEdit = updateDriverEditOdf({
          odfDriverCode: value,
          driverEdit,
          drivers,
        });
      else
        newDriverEdit = updateDriverEditDriver({
          driverCode: value,
          driverEdit,
          drivers,
        });

      const { driverCode: newDriver, plannedOdf: newOdf } = newDriverEdit;
      const { plannedDriverCode: newOdfPreEditPlannedDriver } =
        drivers[newOdf || newDriver] || {};
      const { plannedOdf: newDriverPreEditPlannedOdf } =
        drivers[newDriver] || {};
      const { routeCode: newOdfPreEditRoute } =
        drivers[newOdfPreEditPlannedDriver || newOdf] || {};
      const { routeCode: newDriverPreEditRoute } = drivers[newDriver] || {};
      routeDayToLock.add(newOdfPreEditRoute);
      routeDayToLock.add(newDriverPreEditRoute);
      driverDayToLock.add(newOdfPreEditPlannedDriver);
      driverDayToLock.add(newDriverPreEditPlannedOdf);
      driverDayToLock.add(newDriver);
      driverDayToLock.add(newOdf);
    } else if (name === "routeCode") {
      driverDayToUnlock.add(currentRoutePreEditDriver);
      driverDayToUnlock.add(currentRoutePreEditOdf);
      routeDayToUnlock.add(currentRoute);

      newDriverEdit = {
        ...driverEdit,
        [name]: [undefined, ""].includes(value) ? null : value,
      };

      const {
        driverCode: newRoutePreEditDriver,
        plannedOdf: newRoutePreEditOdf,
      } = driversByRouteCode[newDriverEdit.routeCode] || {};

      driverDayToLock.add(newRoutePreEditDriver);
      driverDayToLock.add(newRoutePreEditOdf);
      routeDayToLock.add(newDriverEdit.routeCode);
    } else
      newDriverEdit = {
        ...driverEdit,
        [name]: [undefined, ""].includes(value) ? null : value,
      };

    routeDayToLock = [...routeDayToLock].filter((routeCode) => !!routeCode);
    routeDayToUnlock = [...routeDayToUnlock].filter(
      (routeCode) => routeCode && !routeDayToLock.includes(routeCode)
    );
    driverDayToLock = [...driverDayToLock].filter((driverCode) => !!driverCode);
    driverDayToUnlock = [...driverDayToUnlock].filter(
      (driverCode) => driverCode && !driverDayToLock.includes(driverCode)
    );

    promises = await Promise.allSettled([
      ...routeDayToLock.map((routeCode) => onLockRouteDay(routeCode)),
      ...driverDayToLock.map((driverCode) => onLockDriverDay(driverCode)),
    ]);
    failToLock = promises.find(({ status }) => status !== "fulfilled");
    if (failToLock) throw new Error("Fail to lock");

    // Do not rethrow error on unlock, if the unlock fails but all
    // lock succeed allow edit to continue.
    await Promise.allSettled([
      ...routeDayToUnlock.map((routeCode) => onUnlockRouteDay(routeCode)),
      ...driverDayToUnlock.map((driverCode) => onUnlockDriverDay(driverCode)),
    ]);
    return newDriverEdit;
  } finally {
    await onFinally();
  }
};

export const updateDriverEditOdf = ({ odfDriverCode, driverEdit, drivers }) => {
  if (DR_DRIVER_TYPE !== driverEdit.driverType)
    return {
      ...drivers[odfDriverCode],
      routeCode: driverEdit.routeCode,
    };
  else if (!odfDriverCode)
    return {
      ...driverEdit,
      plannedOdf: null,
    };

  const odf = drivers[odfDriverCode];
  return {
    ...driverEdit,
    plannedOdf: odfDriverCode,
    dutyStartTime: odf.dutyStartTime,
    dutyEndTime: odf.dutyEndTime,
    plannedDepartTime: odf.plannedDepartTime,
    plannedReturnTime: odf.plannedReturnTime,
    sectionCode: odf.sectionCode,
    startAtSatellite: odf.startAtSatellite,
    endAtSatellite: odf.endAtSatellite,
    vehicleType: odf.vehicleType,
  };
};

export const updateDriverEditDriver = ({
  driverCode: newDriverCode,
  driverEdit,
  drivers,
}) => {
  if (newDriverCode) {
    const { driverCode, plannedOdf, driverType, routeCode } = driverEdit;
    const newDriver = {
      ...drivers[newDriverCode],
      plannedDriverCode: null,
      plannedOdf: null,
      routeCode,
    };
    if (newDriver.driverType === DR_DRIVER_TYPE) {
      const odfDriverCode =
        driverType === ODF_DRIVER_TYPE ? driverCode : plannedOdf;
      const odf = drivers[odfDriverCode];
      newDriver.plannedOdf = odfDriverCode;
      newDriver.dutyStartTime = odf?.dutyStartTime;
      newDriver.dutyEndTime = odf?.dutyEndTime;
      newDriver.plannedDepartTime = odf?.plannedDepartTime;
      newDriver.plannedReturnTime = odf?.plannedReturnTime;
      newDriver.sectionCode = odf?.sectionCode;
      newDriver.startAtSatellite = odf?.startAtSatellite;
      newDriver.endAtSatellite = odf?.endAtSatellite;
      newDriver.vehicleType = odf?.vehicleType;
    }
    return newDriver;
  } else if (driverEdit.driverType === ODF_DRIVER_TYPE)
    return {
      ...driverEdit,
      plannedOdf: driverEdit.driverCode,
      driverType: null,
      driverCode: null,
    };
  else
    return {
      ...driverEdit,
      driverCode: null,
    };
};

// DRIVER MANAGEMENT - DRIVER PROFILE UPDATE
export const updateDriverProfile = ({ updates = [], driverProfileEdit }) => {
  const driverTypeUpdate = updates.find(({ name }) => name === "driverType");
  const alternativeStartLocationUpdate = updates.find(
    ({ name }) => name === "alternativeStartLocation"
  );
  const alternativeEndLocationUpdate = updates.find(
    ({ name }) => name === "alternativeEndLocation"
  );

  if (
    (driverTypeUpdate && driverTypeUpdate.value !== CHOICE_DRIVER_TYPE) ||
    (alternativeStartLocationUpdate && !alternativeStartLocationUpdate.value)
  ) {
    updates.push({ name: "alternativeStartLocation", value: false });
    updates.push({ name: "startIsManual", value: null });
    updates.push({ name: "startOrganisationName", value: null });
    updates.push({ name: "startAddressLine1", value: null });
    updates.push({ name: "startAddressLine2", value: null });
    updates.push({ name: "startCity", value: null });
    updates.push({ name: "startCounty", value: null });
    updates.push({ name: "startPostcode", value: null });
    updates.push({ name: "startLatitude", value: null });
    updates.push({ name: "startLongitude", value: null });
  }

  if (
    (driverTypeUpdate && driverTypeUpdate.value !== CHOICE_DRIVER_TYPE) ||
    (alternativeEndLocationUpdate && !alternativeEndLocationUpdate.value)
  ) {
    updates.push({ name: "alternativeEndLocation", value: false });
    updates.push({ name: "endIsManual", value: null });
    updates.push({ name: "endOrganisationName", value: null });
    updates.push({ name: "endAddressLine1", value: null });
    updates.push({ name: "endAddressLine2", value: null });
    updates.push({ name: "endCity", value: null });
    updates.push({ name: "endCounty", value: null });
    updates.push({ name: "endPostcode", value: null });
    updates.push({ name: "endLatitude", value: null });
    updates.push({ name: "endLongitude", value: null });
  }

  const newDriverProfileEdit = updates.reduce(
    (acc, { name, value }) => {
      acc[name] = value;
      return acc;
    },
    {
      ...driverProfileEdit,
    }
  );

  return newDriverProfileEdit;
};

// TODO check whether this is correct. Unlike total duty time in modal this does include driver break
export const getTotalDuty = (driver) => {
  const { dutyStartTime, dutyEndTime } = driver || {};
  return getDiffInHHMM(dutyStartTime, dutyEndTime);
};

export const getDriverlessRoutes = (routes, planType) =>
  routes
    .filter((route) => {
      const { routeCode, driverCode, routeType } = route;
      if (driverCode || !isNonRedirectionRouteCode(routeCode)) return false;
      else if (
        [OVERRIDES_PLAN_TYPE, PICKUP_PLAN_TYPE, LOCATIONS_PLAN_TYPE].includes(
          planType
        )
      )
        return routeHasTerritory(route, planType);
      else if (planType !== COLLECTIONS_PLAN_TYPE)
        return planType === routeType && routeHasTerritory(route, planType);
      else
        return (
          routeHasTerritory(route, planType, PLANNED) ||
          routeHasTerritory(route, planType, SAME_DAY)
        );
    })
    .sort(({ routeCode: a }, { routeCode: b }) => a.localeCompare(b));

export const generateDriverProfilesConfig = ({ drivers, driverProfileLocks }) =>
  drivers.map(({ driverCode }) => ({
    driverCode,
    checksum: driverProfileLocks[driverCode].checksum,
    lockedByUserCode: driverProfileLocks[driverCode].lockedByUserCode,
    lockedByUserName: driverProfileLocks[driverCode].lockedByUserName,
    lockedDatetime: driverProfileLocks[driverCode].lockedDatetime,
  }));

export const generateDriverDaysConfig = ({ drivers, driverDayLocks }) =>
  drivers.map(
    ({
      driverCode,
      driverType,
      plannedOdf,
      plannedDriverCode,
      routeCode,
      deliveryStopTime,
      collectionStopTime,
      dutyStartTime,
      dutyEndTime,
      plannedDepartTime,
      plannedReturnTime,
      sectionCode,
      startAtSatellite,
      endAtSatellite,
    }) => ({
      driverCode,
      driverType,
      plannedOdf,
      plannedDriverCode,
      routeCode,
      deliveryStopTime,
      collectionStopTime,
      dutyStartTime,
      dutyEndTime,
      plannedDepartTime,
      plannedReturnTime,
      sectionCode,
      startAtSatellite,
      endAtSatellite,
      checksum: driverDayLocks[driverCode].checksum,
      lockedByUserCode: driverDayLocks[driverCode].lockedByUserCode,
      lockedByUserName: driverDayLocks[driverCode].lockedByUserName,
      lockedDatetime: driverDayLocks[driverCode].lockedDatetime,
    })
  );

export const getDriverDayEdits = ({ driverCode, matrixDays = {} }) =>
  [0, 1, 2, 3, 4, 5, 6].reduce((acc, dayOfWeek) => {
    const driverDay = matrixDays[dayOfWeek].drivers[driverCode];
    if (
      driverDay.driverType === ODF_DRIVER_TYPE &&
      driverDay.plannedDriverCode
    ) {
      const plannedDriverDay =
        matrixDays[dayOfWeek].drivers[driverDay.plannedDriverCode];
      acc[dayOfWeek] = { ...plannedDriverDay };
    } else acc[dayOfWeek] = { ...driverDay };
    return acc;
  }, {});

export const getDriverDayFormData = ({
  driverEdit,
  vehicles,
  drivers,
  routes,
  depotDriverValues,
}) => {
  const driverEditWithTotals = calculateDriverEditTotals({
    driverEdit,
    vehicles,
  });
  const routesMap = routes.reduce((acc, route) => {
    acc[route.routeCode] = route;
    return acc;
  }, {});
  let errors;
  ({ driverEdit, errors } = validateDriverEdit({
    depotDriverValues,
    routesMap,
    driverEdit: driverEditWithTotals,
  }));

  const selectedRoute =
    !errors.routeCode &&
    driverEdit.routeCode &&
    routesMap[driverEdit.routeCode];

  let requiredDriverType = null;
  if (selectedRoute?.routeType === CHOICE_PLAN_TYPE)
    requiredDriverType = CHOICE_DRIVER_TYPE;
  else if (selectedRoute?.routeType === ROBOTS_PLAN_TYPE)
    requiredDriverType = ROBOT_DRIVER_TYPE;

  return {
    driverEdit,
    errors,
    hasErrors: !!Object.keys(errors).length,
    odfCode: getDriverOdfCode({ driverEdit }) || null,
    selectableOdfs: getDriverSelectableOdfs({ drivers, requiredDriverType }),
    selectableDrivers: getOdfSelectableDrivers({
      driverEdit,
      drivers,
      requiredDriverType,
    }),
  };
};

export const getDriverSelectableOdfs = ({ drivers, requiredDriverType }) =>
  !requiredDriverType
    ? Object.keys(drivers)
        .reduce((acc, driverCode) => {
          const driver = drivers[driverCode];
          if (driver.driverType === ODF_DRIVER_TYPE) acc.push(driver);
          return acc;
        }, [])
        .sort(({ driverCode: a }, { driverCode: b }) => a.localeCompare(b))
    : [];

export const getDriverOdfCode = ({ driverEdit }) =>
  driverEdit.driverType === ODF_DRIVER_TYPE
    ? driverEdit.driverCode
    : driverEdit.plannedOdf || "";

export const getOdfSelectableDrivers = ({
  driverEdit,
  drivers,
  requiredDriverType,
}) => {
  const { driverCode: driverEditCode } = driverEdit;
  const odfDriverCode = getDriverOdfCode({ driverEdit });
  let isDriverForOdf;
  if (!odfDriverCode)
    isDriverForOdf = ({ driverCode, driverType }) =>
      ODF_DRIVER_TYPE !== driverType &&
      (DR_DRIVER_TYPE !== driverType || driverEditCode === driverCode);
  else
    isDriverForOdf = ({ driverCode, driverType }) =>
      driverType === DR_DRIVER_TYPE ||
      (driverType === ODF_DRIVER_TYPE && driverCode === odfDriverCode);

  return Object.keys(drivers)
    .reduce((acc, driverCode) => {
      const driver =
        driverCode === driverEdit.driverCode ? driverEdit : drivers[driverCode];
      if (driver.driverType === requiredDriverType) acc.push(driver);
      else if (!requiredDriverType && isDriverForOdf(driver)) acc.push(driver);
      return acc;
    }, [])
    .sort(({ driverCode: a }, { driverCode: b }) => a.localeCompare(b));
};

export const calculateDriverEditTotals = ({ driverEdit, vehicles }) => {
  const {
    dutyStartTime,
    dutyEndTime,
    plannedDepartTime,
    plannedReturnTime,
    vehicleType,
  } = driverEdit;
  const { breakTime = 1800, maxDutyTime = 41400 } = vehicles[vehicleType] || {};

  const breakTimeMinutes = breakTime / 60;
  const dutyFormatValid =
    isHHMMFormat(dutyStartTime) && isHHMMFormat(dutyEndTime);
  let totalDutyTimeMinutes = getDiffInMinutes(dutyStartTime, dutyEndTime);
  totalDutyTimeMinutes -= breakTimeMinutes;
  if (totalDutyTimeMinutes < 0) totalDutyTimeMinutes = 0;
  return {
    ...driverEdit,
    maxDutyTime: formatSecondsToHHMM(maxDutyTime - breakTime),
    totalDutyTime: dutyFormatValid
      ? formatMinutesToHHMM(totalDutyTimeMinutes)
      : "",
    amTimeInDepot: getDiffInHHMM(dutyStartTime, plannedDepartTime),
    pmTimeInDepot: getDiffInHHMM(plannedReturnTime, dutyEndTime),
  };
};

export const validateDriverEdit = ({
  depotDriverValues: { minDutyTime, maxAmTimeInDepot, maxPmTimeInDepot },
  routesMap = {},
  driverEdit,
}) => {
  const errors = {};
  const {
    driverCode,
    plannedOdf,
    driverType,
    dutyStartTime,
    dutyEndTime,
    plannedDepartTime,
    plannedReturnTime,
    deliveryStopTime,
    collectionStopTime,
    sectionCode,
    maxDutyTime,
    totalDutyTime,
    amTimeInDepot,
    pmTimeInDepot,
    routeCode,
  } = driverEdit || {};
  const hasRegularRouteCode = isNonRedirectionRouteCode(routeCode);
  const route = routesMap[routeCode];
  const {
    routeType,
    overridesAllocated,
    pickupAllocated,
    locationRoute,
    plannedRoute,
    sameDayRoute,
  } = route || {};
  if (!driverCode && !plannedOdf) return { driverEdit, errors };
  else if (!driverCode)
    return {
      driverEdit,
      errors: {
        driverCode: DRIVER_IS_REQUIRED_FOR_AN_ODF,
      },
    };
  else if (!plannedOdf && DR_DRIVER_TYPE === driverType && hasRegularRouteCode)
    return {
      driverEdit,
      errors: {
        plannedOdf: THIS_DRIVER_REQUIRES_AN_ODF,
      },
    };
  else if (hasRegularRouteCode && !route)
    return {
      driverEdit,
      errors: {
        routeCode: ROUTE_NOT_FOUND(routeCode),
      },
    };
  else if (
    hasRegularRouteCode &&
    routeType &&
    // NON-ROBOT DRIVER ON ROBOT ROUTE
    ((ROBOTS_PLAN_TYPE === routeType && ROBOT_DRIVER_TYPE !== driverType) ||
      // ROBOT DRIVER ON NON-ROBOT ROUTE
      (ROBOTS_PLAN_TYPE !== routeType && ROBOT_DRIVER_TYPE === driverType) ||
      // NON-CHOICE DRIVER ON CHOICE ROUTE
      (CHOICE_PLAN_TYPE === routeType && CHOICE_DRIVER_TYPE !== driverType) ||
      // CHOICE DRIVER ON NON-CHOICE ROUTE
      (CHOICE_PLAN_TYPE !== routeType && CHOICE_DRIVER_TYPE === driverType))
  )
    return {
      driverEdit,
      errors: {
        driverCode: DRIVER_NOT_SUITABLE(driverCode),
        routeCode: ROUTE_NOT_SUITABLE(routeCode),
      },
    };
  else if (
    hasRegularRouteCode &&
    // CHOICE OR ROBOT DRIVER ON ROUTE WITH OVERRIDE
    ((overridesAllocated &&
      [CHOICE_DRIVER_TYPE, ROBOT_DRIVER_TYPE].includes(driverType)) ||
      // CHOICE OR ROBOT DRIVER ON PICKUP ROUTE
      (pickupAllocated &&
        [CHOICE_DRIVER_TYPE, ROBOT_DRIVER_TYPE].includes(driverType)) ||
      // CHOICE OR ROBOT DRIVER ON LOCATION ROUTE
      (locationRoute &&
        [CHOICE_DRIVER_TYPE, ROBOT_DRIVER_TYPE].includes(driverType)) ||
      // CHOICE OR ROBOT DRIVER ON ROUTE WITH PLANNED COLLECTION
      (plannedRoute &&
        [CHOICE_DRIVER_TYPE, ROBOT_DRIVER_TYPE].includes(driverType)) ||
      // CHOICE OR ROBOT DRIVER ON ROUTE WITH SAME DAY COLLECTION
      (sameDayRoute &&
        [CHOICE_DRIVER_TYPE, ROBOT_DRIVER_TYPE].includes(driverType)))
  )
    return {
      driverEdit,
      errors: {
        driverCode: DRIVER_NOT_SUITABLE(driverCode),
        routeCode: ROUTE_NOT_SUITABLE(routeCode),
      },
    };
  else if (routeCode && !hasRegularRouteCode)
    return {
      driverEdit,
      errors: {
        routeCode: INVALID_ROUTE_CODE,
      },
    };
  else if (!routeCode)
    return {
      driverEdit,
      errors: {},
    };

  const isValidDutyStartTime = isHHMMFormat(dutyStartTime);
  const isValidDutyEndTime = isHHMMFormat(dutyEndTime);
  const isValidPlannedDepartTime = isHHMMFormat(plannedDepartTime);
  const isValidPlannedReturnTime = isHHMMFormat(plannedReturnTime);
  const isValidDeliveryStopTime = isMMSSFormat(deliveryStopTime);
  const isValidCollectionStopTime = isMMSSFormat(collectionStopTime);
  const isValidTotalDutyTime = isMMSSFormat(totalDutyTime);
  const isValidAmTimeInDepot = isMMSSFormat(amTimeInDepot);
  const isValidPmTimeInDepot = isMMSSFormat(pmTimeInDepot);

  if (!isValidDutyStartTime) errors["dutyStartTime"] = INVALID_TIME_FORMAT;
  else if (isValidDutyEndTime && dutyEndTime <= dutyStartTime)
    errors["dutyStartTime"] = DUTY_START_MUST_BE_BEFORE_DUTY_END;
  else if (isValidPlannedReturnTime && plannedReturnTime <= dutyStartTime)
    errors["dutyStartTime"] = DUTY_START_MUST_BE_BEFORE_RETURN;
  else if (isValidPlannedDepartTime && plannedDepartTime <= dutyStartTime)
    errors["dutyStartTime"] = DUTY_START_MUST_BE_BEFORE_DEPART;

  if (!isValidDutyEndTime) errors["dutyEndTime"] = INVALID_TIME_FORMAT;
  else if (isValidDutyStartTime && dutyEndTime <= dutyStartTime)
    errors["dutyEndTime"] = DUTY_END_MUST_BE_AFTER_DUTY_START;
  else if (isValidPlannedDepartTime && dutyEndTime <= plannedDepartTime)
    errors["dutyEndTime"] = DUTY_END_MUST_BE_AFTER_DEPART;
  else if (isValidPlannedReturnTime && dutyEndTime <= plannedReturnTime)
    errors["dutyEndTime"] = DUTY_END_MUST_BE_AFTER_RETURN;

  if (!isValidPlannedDepartTime)
    errors["plannedDepartTime"] = INVALID_TIME_FORMAT;
  else if (isValidPlannedReturnTime && plannedReturnTime <= plannedDepartTime)
    errors["plannedDepartTime"] = DEPART_MUST_BE_BEFORE_RETURN;
  else if (isValidDutyStartTime && dutyStartTime >= plannedDepartTime)
    errors["plannedDepartTime"] = DEPART_MUST_BE_AFTER_DUTY_START;
  else if (isValidDutyEndTime && dutyEndTime <= plannedDepartTime)
    errors["plannedDepartTime"] = DEPART_MUST_BE_BEFORE_DUTY_END;

  if (!isValidPlannedReturnTime)
    errors["plannedReturnTime"] = INVALID_TIME_FORMAT;
  else if (isValidPlannedDepartTime && plannedReturnTime <= plannedDepartTime)
    errors["plannedReturnTime"] = RETURN_MUST_BE_AFTER_DEPART;
  else if (isValidDutyStartTime && dutyStartTime >= plannedReturnTime)
    errors["plannedReturnTime"] = RETURN_MUST_BE_AFTER_DUTY_START;
  else if (isValidDutyEndTime && dutyEndTime <= plannedReturnTime)
    errors["plannedReturnTime"] = RETURN_MUST_BE_BEFORE_DUTY_END;

  if (!isValidDeliveryStopTime)
    errors["deliveryStopTime"] = INVALID_STOP_TIME_FORMAT;
  if (!isValidCollectionStopTime)
    errors["collectionStopTime"] = INVALID_STOP_TIME_FORMAT;

  if (!sectionCode && hasRegularRouteCode)
    errors["sectionCode"] = SECTION_IS_REQUIRED;

  if (isValidTotalDutyTime && totalDutyTime < minDutyTime)
    errors["totalDutyTime"] = TOTAL_DUTY_MUST_BE_AT_LEAST(minDutyTime);
  else if (isValidTotalDutyTime && totalDutyTime > maxDutyTime)
    errors["totalDutyTime"] = TOTAL_DUTY_EXCEEDS_MAX_DUTY(maxDutyTime);

  if (isValidAmTimeInDepot && amTimeInDepot > maxAmTimeInDepot)
    errors["amTimeInDepot"] =
      AM_TIME_IN_DEPOT_CANT_BE_GREATER(maxAmTimeInDepot);
  if (isValidPmTimeInDepot && pmTimeInDepot > maxPmTimeInDepot)
    errors["pmTimeInDepot"] =
      PM_TIME_IN_DEPOT_CANT_BE_GREATER(maxPmTimeInDepot);

  return {
    driverEdit,
    errors,
  };
};

export const validateDriverProfileEdit = ({
  driverEdit,
  lookupTables,
  excludeDriversMap,
  driverPrefixes,
}) => {
  const {
    odfDriverCodeRegex,
    drDriverCodeRegex,
    choiceDriverCodeRegex,
    robotDriverCodeRegex,
  } = getDriverCodeRegexes(driverPrefixes);
  const errors = {};
  const { driverProfileEdit } = driverEdit;
  const {
    phoneNumber,
    driverType,
    vehicleType,
    startDate,
    averageDeliveriesPerWeek,
  } = driverProfileEdit || {};

  if (!driverProfileEdit.driverCode)
    errors["driverCode"] = "Driver code cannot be empty.";
  else if (
    excludeDriversMap &&
    !!excludeDriversMap[driverProfileEdit.driverCode]
  )
    errors["driverCode"] = "Driver code is already in use.";
  else if (!newDriverCodeRegex.test(driverProfileEdit.driverCode))
    errors["driverCode"] =
      "Invalid code format. Code must contain only capital letters or numbers.";
  else if (
    driverProfileEdit.driverType !== ODF_DRIVER_TYPE &&
    odfDriverCodeRegex.test(driverProfileEdit.driverCode)
  )
    errors["driverCode"] =
      "Invalid code format. Non-odf driver cannot use ODF driver code format.";
  else if (
    driverProfileEdit.driverType !== DR_DRIVER_TYPE &&
    drDriverCodeRegex.test(driverProfileEdit.driverCode)
  )
    errors["driverCode"] =
      "Invalid code format. Non-odf driver cannot use DR driver code format.";
  else if (
    driverProfileEdit.driverType !== CHOICE_DRIVER_TYPE &&
    choiceDriverCodeRegex.test(driverProfileEdit.driverCode)
  )
    errors["driverCode"] =
      "Invalid code format. Non-connect driver cannot use Connect driver code format.";
  else if (
    driverProfileEdit.driverType !== ROBOT_DRIVER_TYPE &&
    robotDriverCodeRegex.test(driverProfileEdit.driverCode)
  )
    errors["driverCode"] =
      "Invalid code format. Non-robot driver cannot use robot driver code format.";

  // Phone Number
  if (
    driverProfileEdit.driverType !== ROBOT_DRIVER_TYPE &&
    (!phoneNumber || phoneNumber.length === 0)
  )
    errors["phoneNumber"] = PHONE_NUMBER_IS_REQUIRED;
  else if (
    driverProfileEdit.driverType !== ROBOT_DRIVER_TYPE &&
    !phoneNumberRegex.test(phoneNumber)
  )
    errors["phoneNumber"] = INVALID_PHONE_NUMBER;

  //Driver Type
  if (!driverType || driverType === NONE)
    errors["driverType"] = DRIVER_TYPE_IS_REQUIRED;

  //Vehicle Type
  if (driverType !== DR_DRIVER_TYPE && (!vehicleType || vehicleType === NONE))
    errors["vehicleType"] = VEHICLE_TYPE_IS_REQUIRED;

  // Start Date
  if (driverProfileEdit.driverType !== ROBOT_DRIVER_TYPE && !startDate)
    errors["startDate"] = START_DATE_IS_REQUIRED;

  // Average Deliveries Per Week
  if (
    driverProfileEdit.driverType !== ROBOT_DRIVER_TYPE &&
    (!averageDeliveriesPerWeek || averageDeliveriesPerWeek < 1)
  )
    errors["averageDeliveriesPerWeek"] = AVERAGE_DELIVERIES_IS_REQUIRED;

  // Start Location
  if (
    driverProfileEdit.alternativeStartLocation &&
    !driverProfileEdit.startIsManual &&
    (!driverProfileEdit.startLatitude || !driverProfileEdit.startLongitude)
  )
    errors["startLocation"] = "Address is required.";
  else if (
    driverProfileEdit.alternativeStartLocation &&
    !driverProfileEdit.startIsManual &&
    driverProfileEdit.startPostcode &&
    !lookupTables[SECTOR][
      new Postcode(driverProfileEdit.startPostcode).toSector()
    ]
  )
    errors["startPostcode"] = "Please select postcode within depot boundaries.";
  else if (
    driverProfileEdit.alternativeStartLocation &&
    driverProfileEdit.startIsManual
  ) {
    if (!driverProfileEdit.startAddressLine1)
      errors["startAddressLine1"] = "Address line 1 is required.";
    if (!driverProfileEdit.startCity) errors["startCity"] = "City is required.";
    if (!driverProfileEdit.startPostcode)
      errors["startPostcode"] = "Please select on the map.";
  }

  // End Location
  if (
    driverProfileEdit.alternativeEndLocation &&
    !driverProfileEdit.endIsManual &&
    (!driverProfileEdit.endLatitude || !driverProfileEdit.endLongitude)
  )
    errors["endLocation"] = "Address is required.";
  else if (
    driverProfileEdit.alternativeEndLocation &&
    !driverProfileEdit.endIsManual &&
    driverProfileEdit.endPostcode &&
    !lookupTables[SECTOR][
      new Postcode(driverProfileEdit.endPostcode).toSector()
    ]
  )
    errors["endPostcode"] = "Please select postcode within depot boundaries.";
  else if (
    driverProfileEdit.alternativeEndLocation &&
    driverProfileEdit.endIsManual
  ) {
    if (!driverProfileEdit.endAddressLine1)
      errors["endAddressLine1"] = "Address line 1 is required.";
    if (!driverProfileEdit.endCity) errors["endCity"] = "City is required.";
    if (!driverProfileEdit.endPostcode)
      errors["endPostcode"] = "Please select on the map.";
  }

  return { driverEdit, errors };
};

export const isDriverActive = ({ driverProfile, matrixDays }) =>
  [0, 1, 2, 3, 4, 5, 6].some((dayOfWeek) =>
    isDriverDayActive({
      driverProfile,
      matrixDay: matrixDays[dayOfWeek],
    })
  );

// TODO consider whether map of which route has at least 1 postcode allocation can be provided here to further narrow driver showing as active
// to the ones allocated to the routes owning at least one postcode/override/collection rather then any route
export const isDriverDayActive = ({ driverProfile, matrixDay }) => {
  const { driverType, routeCode, plannedDriverCode } =
    matrixDay.drivers[driverProfile.driverCode];
  if (driverType === ODF_DRIVER_TYPE && plannedDriverCode)
    return !!matrixDay.drivers[plannedDriverCode].routeCode;
  else return !!routeCode;
};

export const generateDriverColors = ({ driverProfiles }) =>
  Object.entries(driverProfiles).reduce(
    (acc, [driverCode, driverProfile], index) => {
      acc[driverCode] = { ...driverProfile, ...generateColor(index) };
      return acc;
    },
    {}
  );

export const getDriversImpactedWeekCombined = (driversImpacted) =>
  new Set(
    Object.values(driversImpacted).flatMap((driversDayImpacted) =>
      Object.keys(driversDayImpacted)
    )
  )
    .values()
    .reduce((acc, driverCode) => {
      acc[driverCode] = true;
      return acc;
    }, {});
